import type { ReactNode } from 'react'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Eye,
  FileDown,
  FileText,
  FolderArchive,
  KeyRound,
  Loader2,
  MailCheck,
  Pencil,
  Plus,
  Search,
  Send,
  Shield,
  Trash2,
  X,
  XCircle,
} from 'lucide-react'
import { strToU8, zipSync } from 'fflate'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { useToast } from '../hooks/useToast'
import { useWorkspace } from '../hooks/useWorkspace'

interface SavedComunicazione {
  id: string
  createdAt: string
  updatedAt: string
  tipoComunicazione: string
  tipoComunicazioneDesc: string
  tipoInvio: string
  annoRiferimento: string
  meseRiferimento: string
  codiceFiscaleObbligato: string
  denominazioneObbligato: string
  numRapporti: number
  numAnagrafiche: number
  filename: string
  stato: 'bozza' | 'generato' | 'firmato' | 'inviato'
  deliveryStatus: '' | 'pending' | 'sent' | 'delivered' | 'error'
  deliveryAttempts: number
  deliveryMessageId?: string
  deliveryError?: string
  signedAt?: string
  sentAt?: string
  pecAcceptedAt?: string
  deliveredAt?: string
  errorAt?: string
  deliveryCheckedAt?: string
  lastReceiptType?: string
  hasFile: boolean
}

type StatusFilter = 'all' | 'draft' | 'processing' | 'delivered' | 'error'
type ActionType = 'txt' | 'zip' | 'pdf'
type TimelineTone = 'neutral' | 'progress' | 'success' | 'error'

interface TimelineStep {
  key: string
  label: string
  value?: string
  complete: boolean
  tone: TimelineTone
  icon: ReactNode
}

function formatDateTime(
  value?: string,
  locale = 'it-IT',
  pendingLabel = 'In attesa',
) {
  if (!value) return pendingLabel
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return pendingLabel
  return parsed.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function isDeliveryInFlight(com: SavedComunicazione) {
  if (com.deliveryStatus === 'delivered' || com.deliveryStatus === 'error') return false
  return (
    com.deliveryStatus === 'pending'
    || com.deliveryStatus === 'sent'
    || com.stato === 'inviato'
    || Boolean(com.sentAt)
    || Boolean(com.pecAcceptedAt)
  )
}

function getTimelineSteps(com: SavedComunicazione, isIt: boolean): TimelineStep[] {
  const finalIsError = com.deliveryStatus === 'error'

  return [
    {
      key: 'created',
      label: isIt ? 'Creato' : 'Created',
      value: com.createdAt,
      complete: Boolean(com.createdAt),
      tone: 'neutral',
      icon: <Clock3 className="h-4 w-4" />,
    },
    {
      key: 'signed',
      label: isIt ? 'Firmato' : 'Signed',
      value: com.signedAt,
      complete: Boolean(com.signedAt),
      tone: 'neutral',
      icon: <CheckCircle2 className="h-4 w-4" />,
    },
    {
      key: 'sent',
      label: isIt ? 'Inviato' : 'Sent',
      value: com.sentAt,
      complete: Boolean(com.sentAt),
      tone: 'progress',
      icon: <Send className="h-4 w-4" />,
    },
    {
      key: 'accepted',
      label: isIt ? 'PEC accettata' : 'PEC accepted',
      value: com.pecAcceptedAt,
      complete: Boolean(com.pecAcceptedAt),
      tone: 'progress',
      icon: <MailCheck className="h-4 w-4" />,
    },
    {
      key: 'final',
      label: finalIsError ? (isIt ? 'Errore finale' : 'Final error') : (isIt ? 'Consegnato' : 'Delivered'),
      value: finalIsError ? com.errorAt : com.deliveredAt,
      complete: Boolean(finalIsError ? com.errorAt : com.deliveredAt),
      tone: finalIsError ? 'error' : 'success',
      icon: finalIsError ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />,
    },
  ]
}

function getStatusMeta(com: SavedComunicazione, isIt: boolean) {
  if (com.deliveryStatus === 'delivered') {
    return {
      label: isIt ? 'Consegnato' : 'Delivered',
      description: isIt
        ? 'Flusso completato e ricevuta finale disponibile.'
        : 'Flow completed and final receipt available.',
      chipClasses: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
      iconWrapClasses: 'bg-emerald-100 text-emerald-700',
      panelClasses: 'border-emerald-100 bg-emerald-50/40',
      icon: <CheckCircle2 className="h-4 w-4" />,
    }
  }

  if (com.deliveryStatus === 'error') {
    return {
      label: isIt ? 'Errore' : 'Error',
      description: isIt
        ? 'Richiede attenzione: la consegna non si e conclusa.'
        : 'Needs attention: delivery did not complete.',
      chipClasses: 'border border-rose-200 bg-rose-50 text-rose-700',
      iconWrapClasses: 'bg-rose-100 text-rose-700',
      panelClasses: 'border-rose-100 bg-rose-50/40',
      icon: <AlertTriangle className="h-4 w-4" />,
    }
  }

  if (com.pecAcceptedAt) {
    return {
      label: isIt ? 'PEC accettata' : 'PEC accepted',
      description: isIt
        ? 'Ricevuta di accettazione ricevuta. In attesa di consegna finale.'
        : 'Acceptance receipt received. Waiting for final delivery.',
      chipClasses: 'border border-amber-200 bg-amber-50 text-amber-700',
      iconWrapClasses: 'bg-amber-100 text-amber-700',
      panelClasses: 'border-amber-100 bg-amber-50/50',
      icon: <MailCheck className="h-4 w-4" />,
    }
  }

  if (isDeliveryInFlight(com)) {
    return {
      label: isIt ? 'Monitoraggio attivo' : 'Monitoring live',
      description: isIt
        ? 'Invio PEC in corso con aggiornamento automatico ricevute.'
        : 'PEC delivery in progress with automatic receipt refresh.',
      chipClasses: 'border border-amber-200 bg-amber-50 text-amber-700',
      iconWrapClasses: 'bg-amber-100 text-amber-700',
      panelClasses: 'border-amber-100 bg-amber-50/50',
      icon: <Send className="h-4 w-4" />,
    }
  }

  if (com.stato === 'firmato') {
    return {
      label: isIt ? 'Firmato' : 'Signed',
      description: isIt
        ? 'File pronto per l invio PEC.'
        : 'File is ready for PEC delivery.',
      chipClasses: 'border border-sky-200 bg-sky-50 text-sky-700',
      iconWrapClasses: 'bg-sky-100 text-sky-700',
      panelClasses: 'border-sky-100 bg-sky-50/40',
      icon: <CheckCircle2 className="h-4 w-4" />,
    }
  }

  if (com.stato === 'generato') {
    return {
      label: isIt ? 'Generato' : 'Generated',
      description: isIt
        ? 'Comunicazione pronta per la firma.'
        : 'Communication is ready to be signed.',
      chipClasses: 'border border-indigo-200 bg-indigo-50 text-indigo-700',
      iconWrapClasses: 'bg-indigo-100 text-indigo-700',
      panelClasses: 'border-indigo-100 bg-indigo-50/40',
      icon: <FileText className="h-4 w-4" />,
    }
  }

  return {
    label: isIt ? 'Bozza' : 'Draft',
    description: isIt
      ? 'Comunicazione salvata ma non ancora generata.'
      : 'Communication saved but not generated yet.',
    chipClasses: 'border border-slate-200 bg-slate-50 text-slate-700',
    iconWrapClasses: 'bg-slate-200 text-slate-700',
    panelClasses: 'border-slate-200 bg-slate-50',
    icon: <Shield className="h-4 w-4" />,
  }
}

function matchesStatusFilter(com: SavedComunicazione, filter: StatusFilter) {
  if (filter === 'all') return true
  if (filter === 'delivered') return com.deliveryStatus === 'delivered'
  if (filter === 'error') return com.deliveryStatus === 'error'
  if (filter === 'draft') return com.stato === 'bozza'
  return (
    com.stato === 'generato'
    || com.stato === 'firmato'
    || isDeliveryInFlight(com)
  )
}

function getCommunicationPriority(com: SavedComunicazione) {
  if (com.deliveryStatus === 'error') return 0
  if (isDeliveryInFlight(com)) return 1
  if (com.deliveryStatus === 'delivered') return 2
  if (com.stato === 'bozza') return 4
  return 3
}

function sortCommunications(rows: SavedComunicazione[]) {
  return [...rows].sort((left, right) => {
    const priorityDiff = getCommunicationPriority(left) - getCommunicationPriority(right)
    if (priorityDiff !== 0) return priorityDiff

    const leftTimestamp = new Date(left.deliveryCheckedAt || left.updatedAt || left.createdAt).getTime()
    const rightTimestamp = new Date(right.deliveryCheckedAt || right.updatedAt || right.createdAt).getTime()
    return rightTimestamp - leftTimestamp
  })
}

function getActionFeedbackLabel(type: ActionType, isIt: boolean) {
  if (type === 'txt') return isIt ? 'Preparazione TXT...' : 'Preparing TXT...'
  if (type === 'zip') return isIt ? 'Preparazione ZIP...' : 'Preparing ZIP...'
  return isIt ? 'Apertura report...' : 'Opening report...'
}

function TimelineCardStep({
  step,
  locale,
  pendingLabel,
}: {
  step: TimelineStep
  locale: string
  pendingLabel: string
}) {
  const toneClasses = step.complete
    ? step.tone === 'success'
      ? 'border-emerald-200 bg-emerald-50'
      : step.tone === 'error'
        ? 'border-rose-200 bg-rose-50'
        : step.tone === 'progress'
          ? 'border-amber-200 bg-amber-50'
          : 'border-slate-200 bg-white'
    : 'border-slate-200 bg-white'

  const iconClasses = step.complete
    ? step.tone === 'success'
      ? 'bg-emerald-100 text-emerald-700'
      : step.tone === 'error'
        ? 'bg-rose-100 text-rose-700'
        : step.tone === 'progress'
          ? 'bg-amber-100 text-amber-700'
          : 'bg-slate-100 text-slate-700'
    : 'bg-slate-100 text-slate-400'

  return (
    <div className={`rounded-2xl border p-3 ${toneClasses}`}>
      <div className="flex items-center gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClasses}`}>
          {step.icon}
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {step.label}
        </p>
      </div>
      <p className={`mt-3 text-sm font-medium ${step.complete ? 'text-slate-900' : 'text-slate-400'}`}>
        {step.complete ? formatDateTime(step.value, locale, pendingLabel) : pendingLabel}
      </p>
    </div>
  )
}

function TimelineItem({
  step,
  last,
  locale,
  pendingLabel,
}: {
  step: TimelineStep
  last: boolean
  locale: string
  pendingLabel: string
}) {
  const toneClasses = step.complete
    ? step.tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : step.tone === 'error'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : step.tone === 'progress'
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-white text-slate-700'
    : 'border-dashed border-slate-200 bg-slate-50 text-slate-400'

  return (
    <div className="flex items-start gap-4">
      <div className="flex flex-col items-center">
        <div className={`flex h-9 w-9 items-center justify-center rounded-full border ${toneClasses}`}>
          {step.icon}
        </div>
        {!last && <div className="mt-2 h-12 w-px bg-slate-200" />}
      </div>

      <div className="pt-1">
        <p className="text-sm font-semibold text-slate-950">{step.label}</p>
        <p className={`mt-1 text-sm ${step.complete ? 'text-slate-700' : 'text-slate-400'}`}>
          {step.complete ? formatDateTime(step.value, locale, pendingLabel) : pendingLabel}
        </p>
      </div>
    </div>
  )
}

export default function AdrStorico() {
  const { language } = useLanguage()
  const { currentWorkspace } = useWorkspace()
  const { showToast, confirm } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isIt = language === 'it'
  const locale = isIt ? 'it-IT' : 'en-GB'
  const pendingLabel = isIt ? 'In attesa' : 'Pending'

  const [searchFilter, setSearchFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [certDismissed, setCertDismissed] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [actionState, setActionState] = useState<{ id: string; type: ActionType } | null>(null)

  const { data: certStatus } = useQuery<{ configured: boolean; uploadedAt?: string }>({
    queryKey: ['sid-certificato'],
    queryFn: async () => {
      const response = await api.get('/sid/certificato')
      return response.data.data
    },
  })

  const { data: comunicazioni = [], isLoading } = useQuery<SavedComunicazione[]>({
    queryKey: ['sid-comunicazioni', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/sid/comunicazioni')
      return response.data.data || []
    },
    enabled: !!currentWorkspace?.id,
    refetchInterval: (query) => {
      const rows = query.state.data as SavedComunicazione[] | undefined
      if (!rows) return false
      return rows.some((row) => isDeliveryInFlight(row)) ? 15000 : false
    },
  })

  const { data: selectedComunicazione, isFetching: isFetchingDetail } = useQuery<SavedComunicazione>({
    queryKey: ['sid-comunicazione', selectedId],
    queryFn: async () => {
      const response = await api.get(`/sid/comunicazioni/${selectedId}`)
      return response.data.data
    },
    enabled: !!selectedId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/sid/comunicazioni/${id}`),
    onSuccess: async (_data, id) => {
      await queryClient.invalidateQueries({ queryKey: ['sid-comunicazioni'] })
      if (selectedId === id) setSelectedId(null)
      showToast('success', isIt ? 'Comunicazione eliminata.' : 'Communication deleted.')
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : (isIt ? 'Eliminazione fallita.' : 'Delete failed.')
      showToast('error', message)
    },
  })

  const filtered = comunicazioni.filter((com) => {
    if (!matchesStatusFilter(com, statusFilter)) return false

    if (!searchFilter) return true
    const query = searchFilter.toLowerCase()
    return (
      com.codiceFiscaleObbligato?.toLowerCase().includes(query)
      || com.denominazioneObbligato?.toLowerCase().includes(query)
      || com.tipoComunicazioneDesc?.toLowerCase().includes(query)
      || com.filename?.toLowerCase().includes(query)
    )
  })

  const sortedFiltered = sortCommunications(filtered)
  const deliveredCount = comunicazioni.filter((item) => item.deliveryStatus === 'delivered').length
  const errorCount = comunicazioni.filter((item) => item.deliveryStatus === 'error').length
  const draftCount = comunicazioni.filter((item) => item.stato === 'bozza').length
  const processingCount = comunicazioni.filter((item) => matchesStatusFilter(item, 'processing')).length
  const liveCount = comunicazioni.filter((item) => isDeliveryInFlight(item)).length
  const latestCheckValue = comunicazioni.reduce<string | undefined>((latest, item) => {
    const candidate = item.deliveryCheckedAt || item.updatedAt || item.createdAt
    if (!candidate) return latest
    if (!latest) return candidate
    return new Date(candidate).getTime() > new Date(latest).getTime() ? candidate : latest
  }, undefined)

  const filterOptions: Array<{ value: StatusFilter; label: string; count: number }> = [
    { value: 'all', label: isIt ? 'Tutti' : 'All', count: comunicazioni.length },
    { value: 'processing', label: isIt ? 'In corso' : 'In progress', count: processingCount },
    { value: 'delivered', label: isIt ? 'Consegnati' : 'Delivered', count: deliveredCount },
    { value: 'error', label: isIt ? 'Errori' : 'Errors', count: errorCount },
    { value: 'draft', label: isIt ? 'Bozze' : 'Drafts', count: draftCount },
  ]

  async function downloadFile(id: string, filename: string) {
    setActionState({ id, type: 'txt' })
    showToast('info', isIt ? 'Preparazione file TXT...' : 'Preparing TXT file...')

    try {
      const response = await api.get(`/sid/comunicazioni/${id}/download`, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: 'text/plain; charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename || `comunicazione_${id}.txt`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('TXT download failed:', error)
      showToast('error', isIt ? 'Download TXT non riuscito.' : 'TXT download failed.')
    } finally {
      setActionState((current) => (current?.id === id && current.type === 'txt' ? null : current))
    }
  }

  async function downloadZip(id: string, filename: string) {
    setActionState({ id, type: 'zip' })
    showToast('info', isIt ? 'Preparazione archivio ZIP...' : 'Preparing ZIP archive...')

    try {
      const response = await api.get(`/sid/comunicazioni/${id}/download`, { responseType: 'text' })
      const txtContent = response.data
      const baseName = (filename || `comunicazione_${id}.txt`).replace(/\.txt$/i, '')
      const files: Record<string, Uint8Array> = {
        [`${baseName}.txt`]: strToU8(txtContent),
      }

      try {
        const p7mResponse = await api.get(`/sid/comunicazioni/${id}/download-p7m`, { responseType: 'arraybuffer' })
        if (p7mResponse.status === 200 && p7mResponse.data) {
          files[`${baseName}.p7m`] = new Uint8Array(p7mResponse.data as ArrayBuffer)
        }
      } catch {
        // Attachment .p7m is optional.
      }

      const zipData = zipSync(files, { level: 6 })
      const blob = new Blob([zipData.buffer as ArrayBuffer], { type: 'application/zip' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${baseName}.zip`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('ZIP download failed:', error)
      showToast('error', isIt ? 'Download ZIP non riuscito.' : 'ZIP download failed.')
    } finally {
      setActionState((current) => (current?.id === id && current.type === 'zip' ? null : current))
    }
  }

  async function openPdfReport(id: string) {
    setActionState({ id, type: 'pdf' })
    showToast('info', isIt ? 'Apertura report PDF...' : 'Opening PDF report...')

    try {
      const response = await api.get(`/sid/comunicazioni/${id}/pdf`, { responseType: 'text' })
      const popup = window.open('', '_blank')
      if (!popup) {
        showToast('warning', isIt ? 'Consenti i popup per aprire il report.' : 'Allow popups to open the report.')
        return
      }
      popup.document.write(response.data)
      popup.document.close()
    } catch (error) {
      console.error('PDF report failed:', error)
      showToast('error', isIt ? 'Apertura report non riuscita.' : 'Report open failed.')
    } finally {
      setActionState((current) => (current?.id === id && current.type === 'pdf' ? null : current))
    }
  }

  async function handleDelete(id: string) {
    const confirmed = await confirm({
      title: isIt ? 'Eliminare comunicazione?' : 'Delete communication?',
      message: isIt
        ? 'Il file SID e l eventuale .p7m verranno rimossi dallo storico.'
        : 'The SID file and any .p7m attachment will be removed from history.',
      confirmText: isIt ? 'Elimina' : 'Delete',
      cancelText: isIt ? 'Annulla' : 'Cancel',
      type: 'danger',
    })

    if (!confirmed) return
    deleteMutation.mutate(id)
  }

  return (
    <>
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6">
        {!certDismissed && certStatus && !certStatus.configured && (
          <div className="flex items-start gap-3 rounded-[28px] border border-rose-200 bg-white p-5 shadow-sm">
            <XCircle className="mt-0.5 h-6 w-6 flex-shrink-0 text-rose-600" />
            <div className="flex-1">
              <p className="font-semibold text-slate-950">
                {isIt ? 'Certificato SID non configurato' : 'SID certificate not configured'}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {isIt
                  ? 'Per firmare e monitorare l invio PEC in produzione devi caricare il certificato .p12 del Desktop Telematico.'
                  : 'To sign and monitor PEC delivery in production, you must upload the Desktop Telematico .p12 certificate.'}
              </p>
              <Link
                to="/sid/certificato"
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
              >
                <KeyRound className="h-4 w-4" />
                {isIt ? 'Carica certificato' : 'Upload certificate'}
              </Link>
            </div>
            <button
              onClick={() => setCertDismissed(true)}
              className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex flex-col gap-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              <MailCheck className="h-3.5 w-3.5 text-slate-700" />
              {isIt ? 'SID communication dashboard' : 'SID communication dashboard'}
            </div>

            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                {isIt ? 'Dashboard comunicazioni SID' : 'SID communications dashboard'}
              </h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                {isIt
                  ? 'Una vista operativa piu chiara: ogni comunicazione mostra stato, progresso eventi, ultimo check e azioni principali senza ambiguita.'
                  : 'A clearer operating view: every communication shows status, event progress, latest check and primary actions without ambiguity.'}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate('/sid')}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
            {isIt ? 'Nuova comunicazione' : 'New communication'}
          </button>
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              {
                label: isIt ? 'Totale' : 'Total',
                value: comunicazioni.length,
                description: isIt ? 'Comunicazioni archiviate' : 'Archived communications',
                icon: <Shield className="h-4 w-4" />,
                iconClasses: 'bg-slate-100 text-slate-700',
              },
              {
                label: isIt ? 'In corso' : 'In progress',
                value: processingCount,
                description: isIt ? 'Monitoraggio PEC attivo' : 'PEC monitoring is active',
                icon: <Send className="h-4 w-4" />,
                iconClasses: 'bg-amber-100 text-amber-700',
              },
              {
                label: isIt ? 'Consegnate' : 'Delivered',
                value: deliveredCount,
                description: isIt ? 'Flussi completati' : 'Completed deliveries',
                icon: <CheckCircle2 className="h-4 w-4" />,
                iconClasses: 'bg-emerald-100 text-emerald-700',
              },
              {
                label: isIt ? 'Errori' : 'Errors',
                value: errorCount,
                description: isIt ? 'Da verificare subito' : 'Need immediate review',
                icon: <AlertTriangle className="h-4 w-4" />,
                iconClasses: 'bg-rose-100 text-rose-700',
              },
            ].map((metric) => (
              <div key={metric.label} className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-600">{metric.label}</p>
                    <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{metric.value}</p>
                    <p className="mt-2 text-sm text-slate-500">{metric.description}</p>
                  </div>
                  <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${metric.iconClasses}`}>
                    {metric.icon}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                  {isIt ? 'Feedback immediato' : 'Immediate feedback'}
                </p>
                <h2 className="mt-3 text-xl font-semibold text-slate-950">
                  {liveCount > 0
                    ? (isIt ? 'Monitoraggio live attivo' : 'Live monitoring active')
                    : (isIt ? 'Tutto sotto controllo' : 'Everything under control')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {liveCount > 0
                    ? (isIt
                      ? 'Le comunicazioni in corso vengono aggiornate automaticamente ogni 15 secondi. Lo stato resta sempre visibile anche durante la ricezione delle ricevute.'
                      : 'Communications in progress refresh automatically every 15 seconds. Status stays visible while receipts are being collected.')
                    : (isIt
                      ? 'Nessun invio attivo in questo momento. Le comunicazioni consegnate e quelle con errore restano ordinate e leggibili.'
                      : 'No active deliveries right now. Delivered and errored communications remain ordered and easy to scan.')}
                </p>
              </div>

              <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${liveCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {liveCount > 0 ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {isIt ? 'Ultimo aggiornamento' : 'Latest refresh'}
                </p>
                <p className="mt-2 text-sm font-medium text-slate-900">
                  {formatDateTime(latestCheckValue, locale, pendingLabel)}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {isIt ? 'Bozze aperte' : 'Open drafts'}
                </p>
                <p className="mt-2 text-sm font-medium text-slate-900">
                  {draftCount} {isIt ? 'da completare' : 'to complete'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full max-w-xl">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder={isIt ? 'Cerca per CF, denominazione o file...' : 'Search by tax code, name or file...'}
                  value={searchFilter}
                  onChange={(event) => setSearchFilter(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition-colors focus:border-slate-300 focus:bg-white"
                />
              </div>

              <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
                {isIt
                  ? `${sortedFiltered.length} risultati visibili`
                  : `${sortedFiltered.length} visible results`}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold transition-colors ${
                    statusFilter === option.value
                      ? 'bg-slate-950 text-white'
                      : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>{option.label}</span>
                  <span className={`rounded-full px-2 py-0.5 ${statusFilter === option.value ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-700'}`}>
                    {option.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-3 rounded-[28px] border border-slate-200 bg-white py-20 text-slate-500 shadow-sm">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">{isIt ? 'Caricamento comunicazioni...' : 'Loading communications...'}</span>
          </div>
        ) : sortedFiltered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-[28px] border border-slate-200 bg-white py-20 text-center shadow-sm">
            <Shield className="h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              {isIt ? 'Nessuna comunicazione trovata con i filtri correnti.' : 'No communications found with the current filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedFiltered.map((com) => {
              const status = getStatusMeta(com, isIt)
              const timelineSteps = getTimelineSteps(com, isIt)
              const actionFeedback = actionState?.id === com.id ? getActionFeedbackLabel(actionState.type, isIt) : null

              return (
                <article
                  key={com.id}
                  className="rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
                    <div className="min-w-0 flex-1 space-y-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${status.chipClasses}`}>
                              {status.icon}
                              {status.label}
                            </span>

                            {isDeliveryInFlight(com) && (
                              <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
                                <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                                {isIt ? 'Aggiornamento live' : 'Live refresh'}
                              </span>
                            )}

                            {com.deliveryStatus === 'error' && (
                              <span className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                {isIt ? 'Richiede attenzione' : 'Needs attention'}
                              </span>
                            )}
                          </div>

                          <h3 className="mt-4 truncate text-xl font-semibold text-slate-950" title={com.denominazioneObbligato}>
                            {com.denominazioneObbligato}
                          </h3>
                          <p className="mt-1 text-sm text-slate-600">
                            {com.tipoComunicazioneDesc}
                            {' · '}
                            {isIt ? 'Periodo' : 'Period'}
                            {' '}
                            {com.annoRiferimento}/{com.meseRiferimento || '00'}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2 lg:max-w-[420px] lg:justify-end">
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700">
                            {isIt ? 'Ultimo check' : 'Last check'}
                            {': '}
                            {formatDateTime(com.deliveryCheckedAt || com.updatedAt, locale, pendingLabel)}
                          </span>
                          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700">
                            {com.numRapporti} {isIt ? 'rapporti' : 'accounts'}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 text-xs text-slate-600">
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-mono">
                          {com.codiceFiscaleObbligato}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5">
                          {com.filename}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5">
                          {isIt ? 'Creato' : 'Created'}
                          {': '}
                          {formatDateTime(com.createdAt, locale, pendingLabel)}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5">
                          {isIt ? 'Tentativi' : 'Attempts'}
                          {': '}
                          {com.deliveryAttempts || 0}
                        </span>
                      </div>

                      <div className="rounded-[28px] border border-slate-200 bg-slate-50/80 p-4">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-slate-950">
                              {isIt ? 'Timeline eventi' : 'Event timeline'}
                            </h4>
                            <p className="mt-1 text-sm text-slate-600">
                              {isIt
                                ? 'Ogni passaggio chiave del flusso SID resta visibile in ordine operativo.'
                                : 'Every key step of the SID flow stays visible in operational order.'}
                            </p>
                          </div>

                          <span className="inline-flex w-fit rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
                            {com.lastReceiptType || (isIt ? 'Nessuna ricevuta' : 'No receipt')}
                          </span>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-5">
                          {timelineSteps.map((step) => (
                            <TimelineCardStep
                              key={step.key}
                              step={step}
                              locale={locale}
                              pendingLabel={pendingLabel}
                            />
                          ))}
                        </div>
                      </div>

                      {com.deliveryError && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-600" />
                            <div>
                              <p className="text-sm font-semibold text-rose-900">
                                {isIt ? 'Errore consegna' : 'Delivery error'}
                              </p>
                              <p className="mt-1 text-sm text-rose-800">{com.deliveryError}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <aside className={`rounded-[28px] border p-4 xl:w-[290px] ${status.panelClasses}`}>
                      <div className="flex items-start gap-3">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${status.iconWrapClasses}`}>
                          {status.icon}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-950">{status.label}</p>
                          <p className="mt-1 text-sm leading-6 text-slate-600">{status.description}</p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                        <div className="rounded-2xl border border-white/80 bg-white/85 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Message-ID
                          </p>
                          <p className="mt-2 break-all text-sm text-slate-700">
                            {com.deliveryMessageId || 'n/d'}
                          </p>
                        </div>

                        <div className="rounded-2xl border border-white/80 bg-white/85 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            {isIt ? 'Feedback utente' : 'User feedback'}
                          </p>
                          <div className="mt-2 flex min-h-10 items-center">
                            {actionFeedback ? (
                              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                {actionFeedback}
                              </span>
                            ) : isDeliveryInFlight(com) ? (
                              <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
                                <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                                {isIt ? 'Stato sincronizzato automaticamente' : 'Status sync runs automatically'}
                              </span>
                            ) : (
                              <span className="text-sm text-slate-500">
                                {isIt ? 'Azioni pronte all uso.' : 'Actions are ready to use.'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setSelectedId(com.id)}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50"
                        >
                          <Eye className="h-4 w-4" />
                          {isIt ? 'Dettaglio' : 'Details'}
                        </button>

                        <button
                          onClick={() => navigate(`/sid?edit=${com.id}`)}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50"
                        >
                          <Pencil className="h-4 w-4" />
                          {isIt ? 'Modifica' : 'Edit'}
                        </button>

                        <button
                          onClick={() => downloadFile(com.id, com.filename)}
                          disabled={!com.hasFile || Boolean(actionFeedback)}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <FileDown className="h-4 w-4" />
                          TXT
                        </button>

                        <button
                          onClick={() => downloadZip(com.id, com.filename)}
                          disabled={!com.hasFile || Boolean(actionFeedback)}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <FolderArchive className="h-4 w-4" />
                          ZIP
                        </button>

                        <button
                          onClick={() => openPdfReport(com.id)}
                          disabled={Boolean(actionFeedback)}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <FileText className="h-4 w-4" />
                          {isIt ? 'Report' : 'Report'}
                        </button>

                        <button
                          onClick={() => handleDelete(com.id)}
                          disabled={deleteMutation.isPending}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2.5 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          {isIt ? 'Elimina' : 'Delete'}
                        </button>
                      </div>
                    </aside>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>

      {selectedId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[32px] bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.24)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                  {isIt ? 'Dettaglio comunicazione' : 'Communication details'}
                </p>
                <h2 className="mt-3 text-2xl font-semibold text-slate-950">
                  {selectedComunicazione?.filename || selectedId}
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  {selectedComunicazione?.denominazioneObbligato || (isIt ? 'Caricamento dettaglio...' : 'Loading details...')}
                </p>
              </div>

              <button
                onClick={() => setSelectedId(null)}
                className="rounded-xl border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {isFetchingDetail && !selectedComunicazione ? (
              <div className="flex items-center justify-center gap-3 py-16 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">{isIt ? 'Aggiornamento stato...' : 'Refreshing status...'}</span>
              </div>
            ) : selectedComunicazione ? (
              <div className="mt-6 space-y-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${getStatusMeta(selectedComunicazione, isIt).chipClasses}`}>
                    {getStatusMeta(selectedComunicazione, isIt).icon}
                    {getStatusMeta(selectedComunicazione, isIt).label}
                  </span>

                  {isDeliveryInFlight(selectedComunicazione) && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
                      <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                      {isIt ? 'Monitoraggio live attivo' : 'Live monitoring active'}
                    </span>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {isIt ? 'Stato corrente' : 'Current status'}
                    </p>
                    <p className="mt-3 text-sm font-semibold text-slate-950">
                      {getStatusMeta(selectedComunicazione, isIt).label}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                      {getStatusMeta(selectedComunicazione, isIt).description}
                    </p>
                  </div>

                  <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Message-ID
                    </p>
                    <p className="mt-3 break-all text-sm text-slate-700">
                      {selectedComunicazione.deliveryMessageId || 'n/d'}
                    </p>
                  </div>

                  <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {isIt ? 'Tentativi invio' : 'Send attempts'}
                    </p>
                    <p className="mt-3 text-sm font-semibold text-slate-950">
                      {selectedComunicazione.deliveryAttempts || 0}
                    </p>
                  </div>

                  <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {isIt ? 'Ultimo check' : 'Latest refresh'}
                    </p>
                    <p className="mt-3 text-sm font-semibold text-slate-950">
                      {formatDateTime(
                        selectedComunicazione.deliveryCheckedAt || selectedComunicazione.updatedAt,
                        locale,
                        pendingLabel,
                      )}
                    </p>
                  </div>
                </div>

                {selectedComunicazione.deliveryError && (
                  <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-600" />
                      <div>
                        <p className="text-sm font-semibold text-rose-900">
                          {isIt ? 'Errore consegna' : 'Delivery error'}
                        </p>
                        <p className="mt-1 text-sm text-rose-800">{selectedComunicazione.deliveryError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="rounded-[32px] border border-slate-200 p-5">
                  <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-950">
                        {isIt ? 'Timeline operativa' : 'Operational timeline'}
                      </h3>
                      <p className="mt-1 text-sm text-slate-600">
                        {isIt
                          ? 'Creazione, firma, invio e ricevute sono allineati in un unico pannello cronologico.'
                          : 'Creation, signing, sending and receipts are aligned in a single chronological panel.'}
                      </p>
                    </div>

                    <span className="inline-flex w-fit rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                      {selectedComunicazione.lastReceiptType || (isIt ? 'Nessuna ricevuta' : 'No receipt')}
                    </span>
                  </div>

                  <div className="space-y-1">
                    {getTimelineSteps(selectedComunicazione, isIt).map((step, index, steps) => (
                      <TimelineItem
                        key={step.key}
                        step={step}
                        last={index === steps.length - 1}
                        locale={locale}
                        pendingLabel={pendingLabel}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center text-sm text-slate-500">
                {isIt ? 'Dettaglio non disponibile.' : 'Details unavailable.'}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
